import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useStatusPickerOptions } from '../hooks/useStatusPickerOptions'
import type { StatusComboBoxProps } from '../type'
import { VariantComboBox } from '@/components/app/combo-box/VariantComboBox'
import { StatusIcon } from '@/components/app/indicators/StatusIcon'
import { getPlatformShortcutKeys } from '@/lib/utils/keyboard'

/**
 * Status picker: grouped by stage, one glyph per stage, digits to jump and
 * "Edit workflow…" at the foot (design 31a).
 *
 * For compact inline status toggles in task lists/cards, use TaskStatusCheckbox instead.
 */
export function StatusComboBox({
  statuses,
  selectedStatusId,
  onStatusChange,
  placeholderText,
  searchText,
  buttonClassName,
  isDisabled = false,
  variant = 'default',
  fontSize = 'xs',
  popoverOpen,
  onPopoverOpenChange,
  triggerProps,
}: StatusComboBoxProps) {
  const { t } = useTranslation('common')
  const shortcutKeys = useMemo(() => getPlatformShortcutKeys('S'), [])
  const mergedTriggerProps = {
    title: triggerProps?.title ?? `${t('picker.status.title')} (${shortcutKeys.join(' + ')})`,
    'data-testid': 'status-select',
    ...triggerProps,
  }

  const { options, action } = useStatusPickerOptions(statuses, selectedStatusId)

  return (
    <VariantComboBox
      options={options}
      selectedValue={selectedStatusId ?? undefined}
      onChange={onStatusChange}
      variant={variant}
      placeholderText={placeholderText ?? t('picker.status.placeholder')}
      searchText={searchText ?? t('picker.status.search')}
      buttonClassName={buttonClassName}
      isDisabled={isDisabled}
      fontSize={fontSize}
      popoverOpen={popoverOpen}
      onPopoverOpenChange={onPopoverOpenChange}
      triggerProps={mergedTriggerProps}
      enableNumericShortcuts
      action={action}
      renderTriggerIcon={(option) => {
        const status = statuses.find((s) => s.id === option?.value)
        return status ? (
<StatusIcon progressType={status.progressType} statusId={status.id} />
        ) : (
          <StatusIcon progressType="BL" />
        )
      }}
      emptyMessage={
        <span className="text-xs text-muted-foreground">
          {t('picker.status.empty')}
        </span>
      }
    />
  )
}
